import { FC, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { 
  Box, Grid, Paper, 
  Typography, TextField, 
  FormHelperText, Checkbox, FormControlLabel
} from "@mui/material";
import { NumericFormatField } from "@gisce/oficina-virtual-components";
import { useForm, Controller, FormProvider } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import AccessTariffSelectField from "./customFields/AccessTariffSelectField";
import ElectricityProductSelect from "./customFields/ElectricityProductSelect";
import FileUploadField from "../../form-fields/FileUploadField";
import RadioGroupField from "../../form-fields/RadioGroupField";
import CouponField from "../../form-fields/CouponField/CouponField";
import { useTranslation } from "react-i18next";
import { RootState } from "@/store";
import useI18n from "@/hooks/useI18n";
import { StepElecContractFormData, IStepForm } from "../types/stepsFormData";
import { useSettingsContext } from "../../Settings/hooks/useSettingsContext";
import { SubmitButtonContext } from "../Contractation";
import { useProducts } from "../hooks/data/useProducts";
import CouponsHandler from "./customFields/CouponsHandler";

const FormContract: FC<IStepForm<StepElecContractFormData>> = ({
  type,
  onSubmit
}) => {

  const submitButtonContext = useContext(SubmitButtonContext);
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const contractationSettings = Settings.contractation?.electricity;
  const { currentLang } = useI18n();

  const contractation = useSelector((state: RootState) => state[`${type}Contractation`]);
  const installationCUPSSIPSData = contractation.steps.installation.cupsSIPSData;

  const schema = z.object({
    power: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    power2: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    power3: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power4: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power5: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    power6: z.number({
      invalid_type_error: t('common:text.required_field')
    }).optional(),
    accessTariff: z.number({
      invalid_type_error: t('common:text.required_field')
    }).min(1, {message: t('common:text.required_field')}),
    selectedProduct: z.number({
      invalid_type_error: t('common:text.required_field')
    }).positive({message: t('common:text.required_field')}),
    promotionalCode: z.string().array(),
    selfcons: z.boolean(),
    selfconsCode: z.string(),
    selfconsAttachments: z.object({
      name: z.string(),
      content: z.string().regex(/^data:.*\/[a-zA-Z]+;base64,/, t('common:text.invalid_attachment_type')),
    })
    .array(),
  }).superRefine((data, ctx) => {
    if (data.power > 15 || data.power2 > 15) {
      const powersCheck: (keyof Pick<
        StepElecContractFormData, 
        "power" | "power2" | "power3" | "power4" | "power5" | "power6"
      >)[] = ["power3", "power4", "power5", "power6"];
      
      powersCheck.map(powerNum => {
        if (!(powerNum in data) || (powerNum in data && ( typeof data[powerNum] !== "number" || data[powerNum] <= 0))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('common:text.required_field'),
            path: [powerNum],
          });
        }
      })

      for (let i = 0; i < powersCheck.length - 1; i++) {
        const current = powersCheck[i];
        const next = powersCheck[i + 1];
  
        if (data[current] !== undefined && data[next] !== undefined && data[next] < data[current]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`common:text.field_must_be_greater_than_previous_field`),
            path: [next],
          });
        }
      }
    }
    
    if (data.selfcons &&
      (contractationSettings?.selfcons?.requireDocumentation ?? true)) {
        if (!data["selfconsAttachments"].length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('common:text.required_field'),
            path: ["selfconsAttachments"],
          });
        }
      }

  });

  const methods = useForm<StepElecContractFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...contractation.steps.contract,
      power: contractation.steps.contract.power ?? installationCUPSSIPSData.power,
      power2: contractation.steps.contract.power2 ?? installationCUPSSIPSData.power2,
      power3: contractation.steps.contract.power3 ?? installationCUPSSIPSData.power3,
      power4: contractation.steps.contract.power4 ?? installationCUPSSIPSData.power4,
      power5: contractation.steps.contract.power5 ?? installationCUPSSIPSData.power5,
      power6: contractation.steps.contract.power6 ?? installationCUPSSIPSData.power6,
      accessTariff: contractation.steps.contract.accessTariff ?? installationCUPSSIPSData.accessTariff ?? '',
      selfcons: contractation.steps.contract.selfcons ?? installationCUPSSIPSData.selfcons,
      selfconsCode: contractation.steps.contract.selfconsCode ?? installationCUPSSIPSData.selfconsCode
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitting }
  } = methods;

  const power = watch("power");
  const power2 = watch("power2");
  const power3 = watch("power3", 0);
  const power4 = watch("power4", 0);
  const power5 = watch("power5", 0);
  const power6 = watch("power6", 0);
  const watchedPromoCode = watch("promotionalCode");
  const watchedAccessTariff = watch("accessTariff");
  const watchedSelectedProduct = watch("selectedProduct");
  const watchedSelfcons = watch("selfcons");
  const watchedSelfconsCode = watch("selfconsCode");

  const onContractSubmit = async (values: StepElecContractFormData) => {
    await onSubmit(values)
  };

  const productsData = useProducts({
    power, 
    selfcons: watchedSelfcons,
    accessTariff: watchedAccessTariff, 
    cups: contractation.steps.installation.cups, 
    promotionalCode: watchedPromoCode,
    lang: currentLang.value
  });

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => {handleSubmit(onContractSubmit)()},
      loading: isSubmitting,
      // disabled: !!errors.selectedProduct || !availableProducts.length
    });
  }, [isSubmitting, handleSubmit]);

  useEffect(() => {
    if (watchedSelfcons && watchedSelfconsCode === '00') {
      if (typeof installationCUPSSIPSData.selfconsCode == "string" && installationCUPSSIPSData.selfconsCode !== '00') {
        setValue("selfconsCode", installationCUPSSIPSData.selfconsCode);
      } else {
        setValue("selfconsCode", '41');
      }
    } else if (!watchedSelfcons && watchedSelfconsCode !== '00') {
        setValue("selfconsCode", '00');
    }
  }, [watchedSelfcons]);

  const sixPowers = ((power && power > 15) || (power2 && power2 > 15));

  const allPowersFull = sixPowers ? 
    (!!(power && power2 && power3 && power4 && power5 && power6)) 
    : (!!(power && power2));

  const powerField = (
    name: keyof Pick<
      StepElecContractFormData, 
      "power" | "power2" | "power3" | "power4" | "power5" | "power6"
    >,
  ) => (<>
    <Controller
      name={name}
      control={control}
      render={({ field }) => <TextField
        sx={{width: '100%'}}
        label={t('common:text.contractation_' + name)}
        InputProps={{
          inputComponent: NumericFormatField,
          inputProps: {
            suffix:" kW",
            allowNegative: false,
            allowedDecimalSeparators: [","],
            decimalScale: 3,
            decimalSeparator: ",",
            thousandSeparator: ".",
          }
        }}
        variant="standard"
        error={!!errors[name]}
        {...field}
        onChange={(e) => {
          field.onChange(Number(e.target.value));
          trigger(name);
        }}
      />}
    />
    {errors[name]?.message && 
      <FormHelperText error={true} id="area-field-error-text">
        {errors[name].message}
      </FormHelperText>
    }
  </>);

  const domesticPowerNames: Parameters<typeof powerField>[0][] = [
    "power", "power2"
  ];

  const domesticPowerFields = <>
    <Grid container item sm={12} spacing={2}>
      {domesticPowerNames.map((powerName) => (
        <Grid item xs={12} sm={6} key={powerName}>
          {powerField(powerName)}
        </Grid>
      ))}
    </Grid>
  </>

  const industrialPowerNames: Parameters<typeof powerField>[0][] = [
    "power3", "power4", "power5", "power6"
  ];

  const industrialPowerFields = <>
    <Grid container item md={12} spacing={2}>
      {industrialPowerNames.map((powerName) => (
          <Grid item xs={12} sm={6} key={powerName}>
            {powerField(powerName)}
          </Grid>
        )
      )}
    </Grid>
  </>

  useEffect(() => {
    if (!sixPowers){
      setValue("power3", 0);
      setValue("power4", 0);
      setValue("power5", 0);
      setValue("power6", 0);
    }
  }, [sixPowers])

  useEffect(() => {
    if(productsData.isSuccess && productsData.data.length) {
      const currentSelectedProductId = productsData.data.find(prod => prod.id === getValues("selectedProduct"))?.id;
      const currentSelectedProductName = productsData.data.find(prod => prod.id === getValues("selectedProduct"))?.name;
      if(currentSelectedProductId) {
        setValue("selectedProduct", currentSelectedProductId);
        setValue("selectedProductName", currentSelectedProductName);
      } else {
        setValue("selectedProduct", -1);
        setValue("selectedProductName", "");
      }
    }
  }, [productsData.data]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onContractSubmit)}>
        {(contractationSettings?.selfcons?.showSellMessage ?? true) &&
          <Typography style={{fontWeight: "bold"}}>
            {t('common:text.contractation_selfcons_message')}
          </Typography>
        }
        <>
          <Grid container spacing={3}>
            {!(contractation.steps.installation.cupsSIPSData.sipsDataAvailable &&
              typeof contractation.steps.installation.cupsSIPSData.power == "number" &&
              typeof contractation.steps.installation.cupsSIPSData.power2 == "number") &&
              <Grid item sm={12} md={6}>
                <Box sx={{ml: {md: 5}, mr: {md: 5}, mb: 2, mt: 2}}>
                  <Grid container spacing={3}>

                    {domesticPowerFields}
                    {sixPowers && industrialPowerFields}

                    {power && power2 && 
                      <Grid item xs={12} sm={6}>
                        <AccessTariffSelectField
                          label={t('common:text.contractation_access_tariff')}
                          power={{power, power2}}
                          gas={false}
                        />
                      </Grid>
                    }
                  </Grid>  
                </Box>
              </Grid>
            }
            { (contractationSettings?.selfcons?.enabled ?? true) &&
              <Grid item sm={12} md={6}>
                <Paper style={{padding: 15}}>
                  { contractationSettings?.selfcons?.radioGroupVariant ?
                    <Controller
                      name={"selfcons"}
                      control={control}
                      render={({ field }) => <RadioGroupField
                        label={
                          <Typography variant="h6" color="primary">
                            {t('common:text.contractation_selfcons')}
                          </Typography>
                        }
                        radioElements={
                          [
                            {label: t("common:text.generic_yes"), val: true, style: {width: "25%"}},
                            {label: t("common:text.generic_no"), val: false, style: {width: "25%"}}
                          ]
                        }
                        meta={{
                          touched: !!errors.selfcons?.message,
                          error: errors.selfcons?.message,
                        }}
                        input={field}
                      />}
                    />
                  :
                    <>
                      <Controller
                        name={"selfcons"}
                        control={control}
                        render={({ field }) => <FormControlLabel
                          control={<Checkbox
                            {...field}
                            checked={field.value}
                            />} 
                          label={
                            <Typography variant="h6" color="primary">
                              {t('common:text.contractation_selfcons')}
                            </Typography>
                          } 
                        />}
                      />
                      {errors.selfcons?.message &&
                        <FormHelperText error={true}>
                          {errors.selfcons.message}
                        </FormHelperText>
                      }
                    </>
                  }
                  {watchedSelfcons && (
                    <Controller
                      name={"selfconsAttachments"}
                      control={control}
                      render={({ field }) => <FileUploadField
                        min={1}
                        max={3}
                        label={t('common:text.contractation_selfcons_document')}
                        hint={t('common:text.contractation_selfcons_helper')}
                        anotherLabel={t('common:text.contractation_selfcons_add')}
                        removeLabel={t('common:text.remove')}
                        meta={{
                          touched: !!errors.selfconsAttachments,
                          error: errors && Array.isArray(errors.selfconsAttachments) && errors.selfconsAttachments.map(err => err?.message),
                        }}
                        input={field}
                      />}
                    />
                  )}
                  {!!(errors.selfconsAttachments) &&
                    <FormHelperText error={true}>
                      {errors.selfconsAttachments.message}
                    </FormHelperText>
                  }
                </Paper>
              </Grid>
            }
          </Grid>
        </>
        {watchedAccessTariff && allPowersFull &&(
          <>
            {Settings?.features?.promotionalCode?.contractation?.enabled &&
              <>
                <Box m={1} style={{marginLeft: 0}} color="textSecondary">
                  <Typography variant="h6" color="primary">
                    {t('common:text.contractation_promotional_code')}
                  </Typography>
                  <Typography variant="body2" style={{marginBottom: 15}}>
                    {t('common:text.contractation_promotional_detail')}
                  </Typography>
                </Box>

                <CouponField 
                  association={{
                    type: "lead",
                    id: contractation.leadId
                  }}
                  checkSearchParams={true}
                  multiple={Settings.features.promotionalCode.contractation.multiple}
                  outterIsSubmitting={isSubmitting} 
                  type={type}
                  submitButtonLabel={t("common:text.contractation_promotional_apply")}
                />

                <CouponsHandler />
              </>
            }

            <Box m={1} style={{marginLeft: 0}} color="textSecondary">
              <Typography variant="h6" color="primary">
                {t('common:text.contractation_new_pricelist')}
              </Typography>
              <Typography variant="body2" style={{marginBottom: 15}}>
                {t('common:text.contractation_current_pressure_warning')}
              </Typography>
            </Box>
            <Box sx={{ml: {md: 5}, mr: {md: 5}}}>
              <Controller
                name={"selectedProduct"}
                control={control}
                render={({ field }) => <ElectricityProductSelect
                  availableProducts={productsData.isSuccess ? productsData.data : []}
                  loading={productsData.isLoading}
                  input={field}
                />}
              />
              {!!(errors.selectedProduct) &&
                <FormHelperText error={true}>
                  {errors.selectedProduct.message}
                </FormHelperText>
              }
            </Box>
          </>
        )}

      </form>
    </FormProvider>
  );
};

export default FormContract;
